<script>
  import { onMount, createEventDispatcher } from 'svelte';
  import { getDaysInMonth, addDays, format, formatISO, compareAsc, compareDesc, differenceInDays, isValid } from 'date-fns';
  import { Spinner } from 'sveltestrap';

  const MIN_UNIX_EPOCH_YEAR = 1970;

  const dispatch = createEventDispatcher();

  export let date = new Date();

  /**
   *  data schema
   *  [{
   *    id: string,
   *    backgroundColor: string,
   *    name: string,
   *    resources: [{
   *      name: string,
   *      entries: [{
   *        name: string,
   *        from: Date,
   *        to: Date,
   *        data: any,
   *        backgroundColor: string
   *      }],
   *    }],
   * }]
  */
  export let data = [];

  let dateString = '';

  let days = [];

  onMount(() => {
    dateString = formatISO(date, { representation: 'date' });
    refreshDays();
  });

  function refreshDays() {
    const formattedDays = [];
    const daysInMonth = getDaysInMonth(date);
    for (let i = 0; i < daysInMonth; i++) {
      const offsetDate = addDays(date, i);
      formattedDays.push(offsetDate);
    }

    days = formattedDays;
  }

  function getEntries(resource, day) {
    return resource.entries
      .filter(e => isOn(day, e.from))
      .sort((a, b) => getEntryLength(b) - getEntryLength(a));
  }

  function getPrecedingEntries(resource, day) {
    return resource.entries
      .filter(e => isBefore(e.from, day) && isOnOrAfter(e.to, day))
      .sort((a, b) => getEntryLength(b) - getEntryLength(a));
  }

  function getEntryLength(entry) {
    return differenceInDays(entry.to, entry.from) + 1;
  }

  function getEntryContent(entry) {
    return entry.name;
  }

  function getBackgroundColor(entry) {
    return entry.backgroundColor;
  }

// ========== Helpers ========== //

  function isBefore(lhs, rhs) {
    return compareDesc(normalizeDay(lhs), normalizeDay(rhs)) === 1;
  }

  function isOnOrAfter(lhs, rhs) {
    return compareAsc(normalizeDay(lhs), normalizeDay(rhs)) >= 0;
  }

  function isOn(lhs, rhs) {
    return compareAsc(normalizeDay(lhs), normalizeDay(rhs)) === 0;
  }

  function normalizeDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
  }

// ========== Events ========== //

  function dateChangedEvent() {
    const enteredDate = document.querySelector('.resource-timeline-grid-container .controls input[type=date]').valueAsDate;
    if (!isValid(enteredDate) || enteredDate.getFullYear() < MIN_UNIX_EPOCH_YEAR) {
      return;
    }

    date = enteredDate;
    refreshDays();
    dispatch('dateChanged', date);
  }

  function moveDateToLeft() {
    const prevDate = addDays(date, -1);
    dateString = formatISO(prevDate, { representation: 'date' });
    setTimeout(() => {
      dateChangedEvent();
    }, 100);
  }

  function moveDateToRight() {
    const nextDate = addDays(date, 1);
    dateString = formatISO(nextDate, { representation: 'date' });
    setTimeout(() => {
      dateChangedEvent();
    }, 100);
  }

  function toggleOverlay(evt) {
    if (evt.target.classList.contains('collapsed')) {
      evt.target.classList.remove('collapsed');
    } else {
      evt.target.classList.add('collapsed');
    }
  }

  function entryClicked(evt) {
    evt.target.parentElement.style.zIndex = evt.target.checked ? 1 : 0;
  }

</script>

<style>
  .column-header,
  .row-header {
    position: sticky;
  }

  .resource-timeline-grid-container {
    width: 100%;
  }

  table.resource-timeline {
    display: block;
    overflow-x: scroll;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 11;
    line-height: 13px;
    font-size: 15px;
  }

  thead td {
    background-color: #223646;
    color: white;
    align-items: center;
    justify-content: center;
  }

  tr {
    display: flex;
    flex-grow: 1;
  }

  td {
    border: 1.5px solid #2135457a;
    width: 100px;
    min-height: 40px;
    background-color: white;
    flex-grow: 0;
    flex-direction: column;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    text-align: center;
    overflow-x: visible;
    padding: 1px;
  }

  td:first-child {
    min-width: 217px;
    position: sticky;
    left: 0;
    z-index: 10;
    font-weight: bold;
    align-items: center;
    justify-content: center;
  }

  td:nth-child(2):not([colspan]):not(.grid-header) {
    background-color: #c9d8e0;
  }

  .entry {
    --length: 1;

    z-index: 0;
    background-color: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: calc((100px * var(--length)) - 4px);
    height: 40px;
    border: 1px solid #bbbbbb;
    cursor: pointer;
    overflow-x: visible;
    overflow-y: hidden;
  }

  .entry > span {
    font-size: 10px;
    line-height: 13px;
  }

  .entry-offset {
    min-height: 40px;
    border: 1px solid transparent;
    z-index: -1;
  }

  .controls {
    display: flex;
    position: relative;
    height: 40px;
    margin-bottom: -40px;
    z-index: 20;
  }

  .controls {
    pointer-events: none;
  }

  .controls * {
    pointer-events: auto;
  }

  .controls button {
    border: none;
    background-color: #dcdde1;
    width: 45px;
  }

  .controls button:last-child {
    margin-left: auto;
  }

  .controls input[type=date] {
    width: 173px;
  }

  .task-overlay {
    position: absolute;
    display: none;
    transform: translateY(40px);
    z-index: 50;
    background: #fff;
    width: 300px;
    border: 1px solid #202020;
    padding: 20px;
    text-align: left;
    margin: 0 auto;
    line-height: 20px;
    font-size: 12px;
    -webkit-box-shadow: 10px 10px 5px 0px rgba(194, 194, 194, 0.47);
    -moz-box-shadow: 10px 10px 5px 0px rgba(194, 194, 194, 0.47);
    box-shadow: 10px 10px 5px 0px rgba(194, 194, 194, 0.47);
  }

  .task-overlay .title {
    border-bottom: 1px solid #eee;
    font-weight: 800;
    text-align: center;
    padding-bottom: 10px;
  }

  .task-overlay > div {
    display: flex;
  }

  .task-overlay > div > span {
    flex: 1;
  }

  .task-overlay-button {
    --length: 1;

    display: block;
    position: relative;
    width: calc(90px * var(--length));
    height: 36px;
    margin-bottom: -36px;
    cursor: pointer;
    z-index: 2;
    opacity: 0;
  }

  .task-overlay-button:checked ~ .task-overlay {
    display: block;
  }

  .loading-spinner {
    width: 100%;
    min-height: 200px;
    margin-bottom: -200px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

</style>

<div class="resource-timeline-grid-container">
  <div class="controls">
    <button on:click={moveDateToLeft}>
      <span class="fa fa-chevron-left"></span>
    </button>
    <input type="date" bind:value={dateString} on:change={dateChangedEvent} />
    <span class="spacer"></span>
    <button on:click={moveDateToRight}>
      <span class="fa fa-chevron-right"></span>
    </button>
  </div>
  <table class="resource-timeline">
    <thead>
      <tr class="column-header row-header">
        <td>Resources</td>
        {#each days as day}
          <td class="grid-header">{format(day, 'eee')} {format(day, 'dd')} <br/> {format(day, 'LLL')}</td>
        {/each}
      </tr>
    </thead>
    <tbody>
      {#each data as group}
        <tr>
          <td style="background-color: {group.backgroundColor}">{group.name}</td>
          <td colspan="{days.length}" style="width: 100%;">&nbsp;</td>
        </tr>
          {#each group.resources as resource}
            <tr>
              <td>{resource.name}</td>
              {#each days as day}
                <td id="{resource.name}-{format(day, 'yyyy-MM-dd')}">
                  {#each getPrecedingEntries(resource, day) as entry}
                    <span class="entry-offset">&nbsp;</span>
                  {/each}
                  {#each getEntries(resource, day) as entry, i}
                    <div class="entry" style="--length: {getEntryLength(entry)}; background-color: {getBackgroundColor(entry)}">
                      <input type="checkbox" class="task-overlay-button" style="--length: {getEntryLength(entry)}" on:click={entryClicked} />
                      <span>{entry.name}</span>
                      <div class="task-overlay">
                        <div class="title">
                          <span>{entry.data.TaskName}</span>
                        </div>
                        {#if entry.data.DataType === 0}
                          <div class="client-RSF">
                            <span>Client RSF:</span>
                            <span>{!!entry.data.ClientRSF ? parseInt(entry.data.ClientRSF || '').toFixed(1) : 'Empty'}</span>
                          </div>
                          <div class="total-floors">
                            <span>Total Floors:</span>
                            <span>{entry.data.TotalFloor || 'Empty'}</span>
                          </div>
                          <div class="total-suits">
                            <span>Total Suites:</span>
                            <span>{entry.data.TotalSuites || 'Empty'}</span>
                          </div>
                          <div class="job-summary">
                            <span>Job Summary:</span>
                            <span>{entry.data.JobPhase || 'Empty'}</span>
                          </div>
                          <div class="job-date">
                            <span>Job Due Date:</span>
                            <span>{entry.data.JobDueDate || 'Empty'}</span>
                          </div>
                        {:else if entry.data.DataType === 1}
                          <div>
                            <span>Time-Off Label:</span>
                            <span>{entry.data.PtoTimeoffLabel || 'Empty'}</span>
                          </div>
                          <div>
                            <span>Start Date:</span>
                            <span>{entry.data.From || 'Empty'}</span>
                          </div>
                          <div>
                            <span>End Date:</span>
                            <span>{entry.data.To || 'Empty'}</span>
                          </div>
                          <div>
                            <span>Total Hours:</span>
                            <span>{entry.data.PtoTotalHours || 'Empty'}</span>
                          </div>
                          <div>
                            <span>Approver:</span>
                            <span>{entry.data.PtoApproverName || 'Empty'}</span>
                          </div>
                          <div>
                            <span>Notes:</span>
                            <span>{entry.data.PtoNotes || 'Empty'}</span>
                          </div>
                        {/if}
                      </div>
                    </div>
                  {/each}
                </td>
              {/each}
            </tr>
          {/each}
      {/each}
    </tbody>
  </table>
</div>
